import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import stripeCheckoutInputs from "@/assets/stripe-checkout-sample-inputs-opt.png";
import stripeCheckoutFull from "@/assets/stripe-checkout-sample-opt.png";
import { codeWasSent, requestSignInCode } from "@/utils/sign-in-code";
import { appendUtmsToPath } from "@/utils/utm";
import { AlertCircle } from "lucide-react";
import AuthModal from "@/components/auth-modal";
import Navbar from "@/components/navbar";

export default function SingleSso() {
  // Add state for form fields and loading
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [redirectMessage, setRedirectMessage] = useState("");
  const [showZoomedImage, setShowZoomedImage] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // Get location to check for redirect information
  const location = useLocation();

  // Animate between the two images only when accordion is open
  useEffect(() => {
    if (!isAccordionOpen) return;

    const timer = setInterval(() => {
      setShowZoomedImage((prev) => !prev);
    }, 3000); // Switch every 3 seconds

    return () => clearInterval(timer);
  }, [isAccordionOpen]);

  // Check if we have redirect information in the location state
  useEffect(() => {
    // Clear any stale authentication data
    sessionStorage.removeItem("tempEmail");
    sessionStorage.removeItem("cleanPhoneNumber");
    sessionStorage.removeItem("phoneNumber");
    sessionStorage.removeItem("verificationSid");

    if (location.state?.from) {
      // Store the redirect path in sessionStorage
      sessionStorage.setItem("redirectAfterLogin", location.state.from);

      // Display redirect message if provided
      if (location.state.message) {
        setRedirectMessage(location.state.message);
      }
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const normalizedEmail = email.trim().toLowerCase();

      // Shared with /confirm-sso's "Send a new code" (backend picked by hostname, same as the code check)
      const result = await requestSignInCode(normalizedEmail);

      // Check if verification was successful
      if (codeWasSent(result)) {
        // Store the email and delivery identifier for verification.
        sessionStorage.setItem("tempEmail", normalizedEmail);
        sessionStorage.setItem("verificationSid", result.sid);
        window.location.href = appendUtmsToPath("/confirm-sso");
      } else {
        // Verification failed, show error
        if (
          result.message === "Customer not found." ||
          result.error?.includes("Account not found")
        ) {
          setError(
            "Account not found. Please check your email or sign up for a new account.",
          );
        } else {
          setError(
            result.message ||
              result.error ||
              "Authentication failed. Please try again.",
          );
        }
      }
    } catch (err) {
      console.error("Error during verification:", err);
      setError(
        "Connection error. Please check your internet connection and try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col pb-[120px] pt-[98px] bg-[#F5F5F5]">
      <Navbar />
      {/* SSO Form Section */}
      <div className="flex-1 flex pt-[32px] flex-col items-center justify-center px-4">
        {/* Icon Section */}
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-50 to-pink-100 flex items-center justify-center shadow-sm">
            <svg
              className="w-8 h-8 text-pink-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
        </div>
        {/* Heading Section */}
        <div className="text-center mb-8 max-w-sm">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
            Lexi.Tips Login
          </h1>
          <p className="text-slate-600 text-base leading-relaxed">
            Enter your email to continue
          </p>
        </div>

        {redirectMessage && (
          <div className="bg-[#FCEDF7] text-[#F30099] text-sm text-center p-3 rounded-lg mb-4 w-full max-w-sm">
            <div className="flex items-center justify-center mb-1">
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <span className="font-medium">Protected Content</span>
            </div>
            {redirectMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-3 items-center">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 leading-snug">{error}</p>
            </div>
          )}
          <div className="space-y-2">
            <label className="text-sm text-gray-600 block">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // Update session storage immediately
                sessionStorage.setItem("tempEmail", e.target.value);
              }}
              placeholder="Enter your email"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#F0A7D6] focus:border-transparent transition-all duration-200"
              name="email"
              autoComplete="email"
              required
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-brand-pink text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#e391c4] transition-all duration-200 disabled:opacity-50 mt-6"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Sending...
              </span>
            ) : (
              "Continue"
            )}
          </button>
        </form>
        {/* Sign up link */}
        <div className="text-center mt-4">
          <p className="text-gray-600">
            Don&apos;t have an account?{" "}
            <button
              onClick={() => setIsAuthModalOpen(true)}
              className="text-brand-pink hover:underline"
            >
              Sign up
            </button>
          </p>
          <Link
            to="/"
            className="block mt-4 text-brand-pink hover:text-gray-800 transition-all duration-200"
          >
            Back to Lexi.Tips Home Page
          </Link>
        </div>

        {/* Auth Modal */}
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
          initialPlan="free"
        />
      </div>
    </div>
  );
}
// test:commit
